import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, BookOpen, MessageSquare, Share2, LogOut, RefreshCw, Search } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHASE_LABELS = {
  "not started": { label: "Not Started", color: "bg-gray-100 text-gray-600" },
  "introduction": { label: "Introduction", color: "bg-blue-50 text-blue-700" },
  "discovery": { label: "Discovery", color: "bg-indigo-50 text-indigo-700" },
  "story_direction": { label: "Story Direction", color: "bg-purple-50 text-purple-700" },
  "storytelling": { label: "Writing", color: "bg-amber-50 text-amber-700" },
  "analysis": { label: "Analysis", color: "bg-teal-50 text-teal-700" },
  "photos": { label: "Adding Photos", color: "bg-rose-50 text-rose-700" },
  "complete": { label: "Completed", color: "bg-green-50 text-green-700" }
};

const STATUS_LABELS = {
  "inactive": { label: "Inactive", color: "bg-gray-100 text-gray-500" },
  "writing": { label: "Active", color: "bg-[#9FB89F]/20 text-[#4A5D4A]" },
  "published": { label: "Published", color: "bg-[#7A9B7E]/20 text-[#2D3A2D]" }
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl p-6"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-[#9FB89F]/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#7A9B7E]" />
      </div>
      <span className="font-sans text-sm text-[#4A5D4A] uppercase tracking-wider">{label}</span>
    </div>
    <p className="font-serif text-4xl font-medium text-[#2D3A2D]">{value}</p>
    {sub && <p className="font-sans text-xs text-[#7A8A7A] mt-1">{sub}</p>}
  </motion.div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_at");

  const fetchDashboard = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchDashboard();
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { stats, stage_breakdown, users } = data;

  const filteredUsers = users
    .filter(u => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === "chapters") return b.chapter_count - a.chapter_count;
      if (sortBy === "messages") return b.message_count - a.message_count;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });

  return (
    <div className="min-h-screen bg-[#F8F6F1]" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-[#F8F6F1]/80 backdrop-blur-xl border-b border-[#D4C9B8]/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <h1 className="font-serif text-lg font-medium text-[#2D3A2D]">My Story Admin</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              className="p-2 rounded-lg hover:bg-[#E5D4C1]/50 transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-4 h-4 text-[#4A5D4A]" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-sans text-[#4A5D4A] hover:bg-[#E5D4C1]/50 transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="stats-grid">
          <StatCard icon={Users} label="Total Users" value={stats.total_users} sub="Registered accounts" />
          <StatCard icon={BookOpen} label="Active Stories" value={stats.users_with_stories} sub={`${stats.total_chapters} total chapters`} />
          <StatCard icon={MessageSquare} label="Messages" value={stats.total_messages} sub="Conversations exchanged" />
          <StatCard icon={Share2} label="Shared" value={users.filter(u => u.has_shared).length} sub="Stories shared publicly" />
        </div>

        {/* Stage Breakdown */}
        {Object.keys(stage_breakdown).length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl p-6 mb-10" data-testid="stage-breakdown">
            <h2 className="font-serif text-xl font-medium text-[#2D3A2D] mb-4">Story Stage Breakdown</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stage_breakdown).map(([stage, count]) => {
                const info = PHASE_LABELS[stage] || { label: stage, color: "bg-gray-100 text-gray-600" };
                return (
                  <div key={stage} className={`${info.color} px-4 py-2 rounded-full font-sans text-sm flex items-center gap-2`}>
                    <span className="font-medium">{info.label}</span>
                    <span className="bg-white/50 px-2 py-0.5 rounded-full text-xs font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl overflow-hidden" data-testid="users-table">
          <div className="p-6 border-b border-[#D4C9B8]/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h2 className="font-serif text-xl font-medium text-[#2D3A2D]">Users ({users.length})</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A8A7A]" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 rounded-xl border border-[#D4C9B8] bg-[#F8F6F1] font-sans text-sm outline-none focus:border-[#7A9B7E] w-56"
                  data-testid="admin-search"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 rounded-xl border border-[#D4C9B8] bg-[#F8F6F1] font-sans text-sm text-[#4A5D4A] outline-none"
                data-testid="admin-sort"
              >
                <option value="created_at">Newest First</option>
                <option value="chapters">Most Chapters</option>
                <option value="messages">Most Messages</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D4C9B8]/30 bg-[#F8F6F1]/50">
                  <th className="text-left px-6 py-3 font-sans text-xs uppercase tracking-wider text-[#7A8A7A]">User</th>
                  <th className="text-left px-6 py-3 font-sans text-xs uppercase tracking-wider text-[#7A8A7A]">Stage</th>
                  <th className="text-left px-6 py-3 font-sans text-xs uppercase tracking-wider text-[#7A8A7A]">Status</th>
                  <th className="text-center px-6 py-3 font-sans text-xs uppercase tracking-wider text-[#7A8A7A]">Chapters</th>
                  <th className="text-center px-6 py-3 font-sans text-xs uppercase tracking-wider text-[#7A8A7A]">Messages</th>
                  <th className="text-center px-6 py-3 font-sans text-xs uppercase tracking-wider text-[#7A8A7A]">Shared</th>
                  <th className="text-left px-6 py-3 font-sans text-xs uppercase tracking-wider text-[#7A8A7A]">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, i) => {
                  const phaseInfo = PHASE_LABELS[user.story_phase] || { label: user.story_phase, color: "bg-gray-100 text-gray-600" };
                  const statusInfo = STATUS_LABELS[user.story_status] || { label: user.story_status, color: "bg-gray-100 text-gray-500" };
                  return (
                    <motion.tr
                      key={user.user_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="border-b border-[#D4C9B8]/20 hover:bg-[#F8F6F1]/50 transition-colors"
                      data-testid={`user-row-${i}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#9FB89F]/30 flex items-center justify-center font-sans text-xs font-bold text-[#4A5D4A]">
                              {user.name?.[0] || "?"}
                            </div>
                          )}
                          <div>
                            <p className="font-sans text-sm font-medium text-[#2D3A2D]">{user.name}</p>
                            <p className="font-sans text-xs text-[#7A8A7A]">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`${phaseInfo.color} px-3 py-1 rounded-full text-xs font-sans font-medium`}>
                          {phaseInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`${statusInfo.color} px-3 py-1 rounded-full text-xs font-sans font-medium`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center font-sans text-sm text-[#2D3A2D] font-medium">
                        {user.chapter_count}
                      </td>
                      <td className="px-6 py-4 text-center font-sans text-sm text-[#2D3A2D] font-medium">
                        {user.message_count}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {user.has_shared ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#7A9B7E]/20">
                            <Share2 className="w-3 h-3 text-[#7A9B7E]" />
                          </span>
                        ) : (
                          <span className="text-[#D4C9B8]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 font-sans text-xs text-[#7A8A7A]">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                      </td>
                    </motion.tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center font-sans text-sm text-[#7A8A7A]">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
